import React from "react";
import {
  Mail,
  Twitter,
  Github,
  Linkedin,
  MoveUp,
  Cloud,
  Sparkles,
} from "lucide-react";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        {/* Primary gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5"></div>

        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-teal-500/8 rounded-full blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-cyan-500/6 rounded-full blur-2xl animate-pulse delay-1000"></div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(134,239,172,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(134,239,172,0.03)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        {/* Main content grid */}
        <div className="mb-12">
          {/* Enhanced brand section */}
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 rounded-xl blur opacity-70 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <img
                    src="/img/ride_cloud.png"
                    alt="icon"
                    className="w-10 h-10 sm:w-10 sm:h-10 filter brightness-0 opacity-90"
                  />
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">
                  RideCloud
                </h2>
                <div className="flex items-center gap-1 mt-1">
                  <Sparkles className="w-3 h-3 text-emerald-400 animate-pulse" />
                  <span className="text-xs text-emerald-400 font-medium">
                    Next-Gen Mobility
                  </span>
                </div>
              </div>
            </div>
            <p className="text-slate-300 text-lg leading-relaxed max-w-md mb-6">
              Smart carpooling made{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400 font-semibold">
                easy — affordable, efficient,
              </span>{" "}
              and convenient rides for everyone.
            </p>
          </div>
        </div>

        {/* Modern bottom section - Enlarged */}
        <div className="relative pt-12 border-t-2 border-gradient-to-r from-transparent via-slate-700/50 to-transparent">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent"></div>

          <div className="flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-12">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-8 text-slate-400 text-lg text-center lg:text-left">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                <p className="text-base">
                  &copy; {new Date().getFullYear()} RideCloud. All rights
                  reserved.
                </p>
              </div>
              <div className="flex items-center gap-6 text-base flex-wrap justify-center lg:justify-start">
                <a
                  href="mailto:team.ridecloud+support@gmail.com"
                  className="hover:text-emerald-300 transition-colors flex items-center gap-2 text-lg"
                >
                  <Mail className="w-5 h-5" />
                  Contact
                </a>
                <span className="text-slate-600 text-xl">•</span>
                <div className="flex gap-4">
                  {[
                    {
                      icon: <Github className="w-6 h-6" />,
                      href: "https://github.com/gauravkoul",
                      label: "GitHub",
                    },
                    {
                      icon: <Linkedin className="w-6 h-6" />,
                      href: "https://www.linkedin.com/in/gaurav-koul-7568a7176/",
                      label: "LinkedIn",
                    },
                  ].map((social, idx) => (
                    <a
                      key={idx}
                      href={social.href}
                      className="text-slate-400 hover:text-emerald-300 transition-colors hover:scale-110 transform p-1"
                      aria-label={social.label}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
              <p className="text-base">
                Crafted with ❤️ by{" "}
                <a
                  href="https://github.com/gauravkoul"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400 font-semibold hover:from-emerald-300 hover:to-teal-300 transition-all duration-300 text-lg"
                >
                  Gaurav Koul
                </a>
              </p>
            </div>

            {/* Enhanced back to top button - Enlarged */}
            <button
              onClick={scrollToTop}
              className="group relative px-8 py-4 bg-slate-800/50 hover:bg-emerald-500/10 text-slate-300 hover:text-emerald-300 rounded-2xl border-2 border-slate-700/50 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/20 hover:scale-110 backdrop-blur-sm text-lg"
            >
              <div className="flex items-center gap-3 font-semibold">
                Back to top
                <MoveUp className="w-6 h-6 transform rotate-45 group-hover:rotate-0 group-hover:translate-y-[-2px] transition-all duration-300" />
              </div>

              {/* Button glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-500/0 via-emerald-500/10 to-teal-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
